import { AuthApi, CageApi, GenotypesApi, KanbanApi, KanbanDetailApi,
    KanbanUnitApi, MouseApi, PositionApi, ProjectApi, StrainTypesApi,
    StrainsApi, TaskApi, TemplateApi, IdentifiedGenotypesApi,
    NotificationApi, BreedPlanApi, UserConfigApi, RegionApi, LogApi} from '@deerland-tech/animal-admin'
// 环境配置文件
import { environment, aadConfig } from './config' 
import axios from 'axios'
import { ElMessage } from 'element-plus'
import { isMsalPopup } from '@/utils/msalEnv'

// 给 SDK 所有 API 实例注入的全局 axios（X-Region 拦截器）
let _regionCode: string | null = null
export const setSdkRegionCode = (code: string) => {
  _regionCode = code
}

axios.interceptors.request.use((config) => {
  const url = config.url || ''
  // 请求已携带 Authorization 时，统一用 sessionStorage 中最新的 token，避免组件里缓存的旧 token 继续使用
  const currentToken = sessionStorage.getItem('token')
  if ((config.headers as any)?.Authorization && currentToken) {
    ;(config.headers as any)['Authorization'] = 'Bearer ' + currentToken
  }
  // /auth/ 下除代理相关接口外不携带区域；proxy / proxy-candidates 需要 X-Region
  const isAuthWithoutRegion = url.includes('/auth/') && !url.includes('/auth/proxy')
  if (_regionCode && !isAuthWithoutRegion) {
    (config.headers as any)['X-Region'] = _regionCode
  }
  return config
})

// 并发 401 时只执行一次 AAD 静默刷新
let _aadRefreshing: Promise<string> | null = null
let _exitingProxy = false

// 代理 token 失效（401）时，恢复发起代理前的原登录态；仅在 proxy 会话下生效。
const exitProxySession = (): boolean => {
  if (sessionStorage.getItem('loginType') !== 'proxy') return false
  const originalToken = sessionStorage.getItem('originalToken') || ''
  const originalLoginType = sessionStorage.getItem('originalLoginType') || ''
  let originalUserInfo: any = {}
  let originalRegionInfo: any = {}
  let originalSelectedRegion: any = {}
  let originalSlidebarData: any = []
  let originalHouseIdleeder: any = {}
  try { originalUserInfo = JSON.parse(sessionStorage.getItem('originalUserInfo') || '{}') } catch (_) {}
  try { originalRegionInfo = JSON.parse(sessionStorage.getItem('originalRegionInfo') || '{}') } catch (_) {}
  try { originalSelectedRegion = JSON.parse(sessionStorage.getItem('originalSelectedRegion') || '{}') } catch (_) {}
  try { originalSlidebarData = JSON.parse(sessionStorage.getItem('originalSlidebarData') || '[]') } catch (_) {}
  try { originalHouseIdleeder = JSON.parse(sessionStorage.getItem('originalHouseIdleeder') || '{}') } catch (_) {}

  sessionStorage.setItem('token', originalToken)
  sessionStorage.setItem('loginType', originalLoginType)
  ;['originalToken', 'originalLoginType', 'originalUserInfo', 'originalRegionInfo',
    'originalSelectedRegion', 'originalSlidebarData', 'originalHouseIdleeder'
  ].forEach((k) => sessionStorage.removeItem(k))

  // 同步更新本地持久化状态，刷新后回到发起代理前的用户/区域
  try {
    const savedState = JSON.parse(localStorage.getItem('state') || '{}')
    savedState.currentuserinfo = originalUserInfo
    savedState.regioninfo = originalRegionInfo
    savedState.selectedRegion = originalSelectedRegion
    savedState.slidebardata = originalSlidebarData
    savedState.houseidleeder = originalHouseIdleeder
    localStorage.setItem('state', JSON.stringify(savedState))
  } catch (_) {}
  return true
}
const refreshAadToken = async (): Promise<string> => {
  if (isMsalPopup()) {
    // 在 MSAL 弹窗内不允许再次调用 acquireToken，否则会被 MSAL 拦截
    throw new Error('MSAL popup 内不刷新 token')
  }
  if (_aadRefreshing) return _aadRefreshing
  _aadRefreshing = (async () => {
    const inst = (window as any).__msalInstance
    if (!inst) throw new Error('MSAL 实例不存在')
    const accounts = inst.getAllAccounts()
    if (!accounts?.length) throw new Error('无可用账号')
    const r = await inst.acquireTokenSilent({
      scopes: ['openid', 'profile', aadConfig.clientId + '/.default'],
      account: accounts[0],
    })
    const aadToken = r.idToken || r.accessToken
    sessionStorage.setItem('token', aadToken)
    return aadToken
  })().finally(() => {
    _aadRefreshing = null
  })
  return _aadRefreshing
}

// AAD token 自动刷新
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const req = error.config
    const message = error.response?.data?.message
    if (typeof message === 'string' && message.toLowerCase().includes('x-region')) {
      ElMessage.error('缺少区域信息，请重新选择区域或刷新页面')
      return Promise.reject(error)
    }
    if (error.response?.status !== 401 || req._retry) return Promise.reject(error)
    // 未带 Authorization 的请求不做刷新重试（如登录接口）
    if (!req.headers?.Authorization) return Promise.reject(error)
    // 登录/AAD 验证与注册类接口不走通用刷新，避免循环
    if (req.url?.includes('/auth/login') || req.url?.includes('/auth/aad/verify') || req.url?.includes('/auth/aad/register')) return Promise.reject(error)
    // 代理模式使用目标用户本地 JWT，禁止用 MSAL 刷新覆盖代理 token；
    // 仅当确认是会话/token 失效时才退出代理；权限不足(operation not permitted)等 401 不退出
    if (sessionStorage.getItem('loginType') === 'proxy') {
      const sessionMsg = error.response?.data?.message
      const sessionInvalid = typeof sessionMsg === 'string' &&
        /expired|invalid|logged out|session|login again|token/i.test(sessionMsg)
      if (sessionInvalid && !_exitingProxy) {
        _exitingProxy = true
        exitProxySession()
        ElMessage.error('代理会话已失效，已退出代理，请重新发起代理')
        setTimeout(() => { location.reload() }, 300)
      }
      return Promise.reject(error)
    }
    if (sessionStorage.getItem('loginType') !== 'aad') return Promise.reject(error)
    if (isMsalPopup()) return Promise.reject(error)
    try {
      await refreshAadToken()
      ElMessage.info('登录凭证已过期，已自动获取新凭证，请重新操作')
    } catch {
      ElMessage.error('登录已过期，请重新登录')
      return Promise.reject(error)
    }
    const refreshError: any = new Error('登录凭证已过期，已自动获取新凭证，请重新操作')
    refreshError.code = 'TOKEN_REFRESHED'
    return Promise.reject(refreshError)
  }
)

// AAD 验证与注册（后端新增接口，SDK 尚未生成）
class ExtendedAuthApi extends AuthApi {
  async aadVerify(options?: any): Promise<any> {
    return this.axios.post(this.basePath + '/auth/aad/verify', null, options || {})
  }
  async aadRegister(region: string, options?: any): Promise<any> {
    return this.axios.post(this.basePath + '/auth/aad/register', { region }, options || {})
  }
  async proxyCandidates(options?: any): Promise<any> {
    return this.axios.get(this.basePath + '/auth/proxy-candidates', options || {})
  }
}

// 创建 SDK API 实例并注入自定义 axios
function createSdkApi<T>(ctor: new (cfg: any) => T, config: any): T {
  const instance = new ctor(config)
  ;(instance as any).axios = axios
  return instance
}

//用户相关接口
export const authApi = createSdkApi(ExtendedAuthApi, { basePath: environment.baseUrl })
//笼子相关接口
export const cageApi = createSdkApi(CageApi, { basePath: environment.baseUrl })
//小鼠相关接口
export const mouseApi = createSdkApi(MouseApi, { basePath: environment.baseUrl })
//笼位相关接口
export const positionApi = createSdkApi(PositionApi, { basePath: environment.baseUrl })
//项目号相关接口
export const projectApi = createSdkApi(ProjectApi, { basePath: environment.baseUrl })
//任务相关接口
export const taskApi = createSdkApi(TaskApi, { basePath: environment.baseUrl })
//模版著录相关接口
export const templateApi = createSdkApi(TemplateApi, { basePath: environment.baseUrl })
//品系相关接口
export const strainsApi = createSdkApi(StrainsApi, { basePath: environment.baseUrl })
//品系类型相关接口
export const strainTypesApi = createSdkApi(StrainTypesApi, { basePath: environment.baseUrl })
//基因型相关接口
export const genotypesApi = createSdkApi(GenotypesApi, { basePath: environment.baseUrl })
//日程看板相关接口
export const kanbanApi = createSdkApi(KanbanApi, { basePath: environment.baseUrl })
export const kanbanDetailApi = createSdkApi(KanbanDetailApi, { basePath: environment.baseUrl })
export const kanbanUnitApi = createSdkApi(KanbanUnitApi, { basePath: environment.baseUrl })
//基因型鉴定相关接口
export const identifiedGenotypesApi = createSdkApi(IdentifiedGenotypesApi, { basePath: environment.baseUrl })
//站内信相关接口
export const notificationApi = createSdkApi(NotificationApi, { basePath: environment.baseUrl })
//繁殖组合相关接口
export const breedplanApi = createSdkApi(BreedPlanApi, { basePath: environment.baseUrl })
//笼位颜色修改接口
export const userconfigApi = createSdkApi(UserConfigApi, { basePath: environment.baseUrl })
//区域相关接口 
export const regionApi = createSdkApi(RegionApi, { basePath: environment.baseUrl})
//日志相关接口
export const logApi = createSdkApi(LogApi, { basePath: environment.baseUrl})
